import { Theme, useTheme } from "@/components/theme/themeProvider"
import {
  Carousel,
  CarouselContent,
  CarouselIndicator,
  CarouselIndicators,
  CarouselItem,
} from "@/components/ui/baseUi/carousel"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { AlertIconDark, AlertIconLight } from "@images/svgIcons/alertIcon"
import { PayoutEnumApi } from "@modules/finances/financesPayout/gateway/payoutApi"
import { useStripeAccountErrors } from "@modules/finances/hooks/useStripeAccountErrors"

type StripeAccountErrorAlertProps = {
  orientation: "vertical" | "horizontal"
} & React.ComponentProps<"div">

export function StripeAccountErrorAlert({ orientation, ...props }: StripeAccountErrorAlertProps) {
  const formattedStripeAccountErrors = useStripeAccountErrors(PayoutEnumApi.ATHLETE)

  if (formattedStripeAccountErrors.length === 0) return

  return (
    <div className={orientation === "vertical" ? "mb-4" : ""} data-testid="stripe-account-errors" {...props}>
      <Carousel className="mx-3" opts={{ align: "start", loop: true }}>
        <CarouselContent>
          {formattedStripeAccountErrors.map((item, index) => (
            <CarouselItem className="w-full" key={index}>
              <StripeAccountErrorItem
                {...item}
                orientation={orientation}
                primaryButton={item.primaryButton}
                secondaryButton={item.secondaryButton}
              />
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselIndicators>
          {formattedStripeAccountErrors.map((_, index) => (
            <CarouselIndicator key={index} index={index} />
          ))}
        </CarouselIndicators>
      </Carousel>
    </div>
  )
}

export type StripeAccountErrorItemType = {
  alertTitle: string
  alertSubtitle?: string
  alertContent: string
  primaryButton?: React.ReactNode
  secondaryButton?: React.ReactNode
}

export const StripeAccountErrorItem: React.FC<
  StripeAccountErrorItemType & { orientation: "vertical" | "horizontal"; size?: "M" | "S" }
> = ({ alertTitle, alertSubtitle, alertContent, primaryButton, secondaryButton, orientation, size = "M" }) => {
  const { theme } = useTheme()

  return (
    <div className="mt-3 flex flex-row overflow-hidden rounded-3xl">
      <div className="h-auto bg-red-500" style={{ minWidth: 10 }} />
      <div
        className={
          "flex w-[calc(100%-10px)] grow rounded-r-3xl" +
          (theme === Theme.OwnerDark ? " bg-neutral-50" : " bg-neutral-100") +
          (orientation === "vertical"
            ? " flex-col items-center justify-center"
            : " flex-row items-center justify-between") +
          (size === "M" ? " gap-4 px-4 py-3" : " gap-3 px-3 py-3")
        }
      >
        <div
          className={cn("flex w-full flex-row items-center justify-center gap-2", {
            "justify-start": orientation === "horizontal",
          })}
        >
          {theme === Theme.OwnerDark ? (
            <AlertIconDark color="currentColor" className="text-red-500" />
          ) : (
            <AlertIconLight color="currentColor" className="text-red-500" />
          )}
          <Text fontStyle={size === "M" ? "TM" : "TS"} className="text-center">
            {alertTitle}
          </Text>
        </div>

        {alertSubtitle && (
          <Text fontStyle="TS" className={orientation === "vertical" ? "self-start" : ""}>
            {alertSubtitle}
          </Text>
        )}

        <Text
          fontStyle={size === "M" ? "BM" : "BS"}
          className={`text-neutral-600 ${orientation === "vertical" ? "self-start" : "w-full"}`}
          style={{ whiteSpace: "pre-line" }}
        >
          {alertContent}
        </Text>

        <div className="flex flex-col gap-2">
          {primaryButton}

          {secondaryButton}
        </div>
      </div>
    </div>
  )
}
